import { inject, Injectable } from '@angular/core';
import { AuthConfig, OAuthService } from 'angular-oauth2-oidc';
import Cookies from 'js-cookie';
import { from, map, of } from 'rxjs';
import { AuthProvider } from '../../interface';
import { ToegangUserInterface } from '../../interface/toegang.user.interface';
import { AuthConfigToken } from '../../ng-security.module';
import { isOneGateEntry } from '../../utils/is-onegate-entry';
import { ClaimsInterface, ISSUERS, LicenseInterface } from './toegang.types';

@Injectable({
  providedIn: 'root',
})
export class ToegangProvider implements AuthProvider {
  readonly #config = inject(AuthConfigToken);

  readonly #oauthService = inject(OAuthService);

  readonly #userIdCookieKey = 'user_id';

  claims: ClaimsInterface | undefined;

  #discoveryDocumentLoaded = false;

  get isLoggedIn() {
    return this.#oauthService.hasValidIdToken();
  }

  get token() {
    return this.#oauthService.getIdToken();
  }

  /**
   * This cookie will be set by the webapp.
   * It contains the user id of the logged in user.
   * We use this to get the platform token from the /platform_token endpoint.
   *
   * TODO: This can be ditched when we run standalone
   */
  get userIdCookie() {
    return Cookies.get(this.#userIdCookieKey);
  }

  hasUserIdCookie() {
    return !!this.userIdCookie;
  }

  getCallbackPath() {
    return this.#config.callbackPath ?? 'oauth/callback';
  }

  initialize() {
    if (this.hasUserIdCookie()) return;
    this.#oauthService.configure(this.#getAuthConfig());
  }

  login() {
    this.#oauthService.restartSessionChecksIfStillLoggedIn();
    this.#oauthService.setupAutomaticSilentRefresh();

    if (this.#oauthService.hasValidIdToken()) {
      if (!this.#discoveryDocumentLoaded) {
        this.#discoveryDocumentLoaded = true;
        this.#oauthService.loadDiscoveryDocument();
      }

      return of(this.user);
    }

    this.#discoveryDocumentLoaded = true;

    return from(this.#oauthService.loadDiscoveryDocumentAndLogin()).pipe(
      map(() => this.user),
    );
  }

  logout() {
    this.#oauthService.logOut(true);
    Cookies.remove(this.#userIdCookieKey);
  }

  get user(): ToegangUserInterface {
    const claims = <ClaimsInterface | undefined>(
      (this.claims ?? this.#oauthService.getIdentityClaims())
    );

    if (!claims) {
      throw Error('Failed to get identity claims');
    }

    return {
      identifier: claims.sub,
      firstName: claims.first_name,
      middleName: claims.tussenvoegsels ?? null,
      lastName: claims.family_name,
      name: ToegangProvider.getNameFromClaims(claims),
      CPIs: ToegangProvider.getLicensesFromClaims(claims),
    };
  }

  #getAuthConfig(): AuthConfig {
    return {
      // Url of the Identity Provider
      issuer: ISSUERS[this.#config.environment],

      // URL of the SPA to redirect the user to after login
      redirectUri: `${window.location.origin}/${this.getCallbackPath()}`,

      // The SPA's id. The SPA is registered with this id at the auth-server
      clientId: this.#config.clientId,

      // Just needed if your auth server demands a secret. In general, this
      // is a sign that the auth server is not configured with SPAs in mind,
      // and it might not enforce further best practices vital for security
      // such applications.
      dummyClientSecret: this.#config.clientSecret,

      responseType: 'code',

      // set the scope for the permissions the client should request
      // The first four are defined by OIDC.
      // Important: Request offline_access to get a refresh token
      // The api scope is an use case specific one
      scope: 'openid',

      showDebugInformation: this.#config.debug ?? false,

      disablePKCE: true,

      customQueryParams: {
        type: isOneGateEntry(window.location.pathname) ? 'NT2' : null,
        claims: JSON.stringify({
          id_token: {
            licenses: null,
            first_name: null,
            tussenvoegsels: null,
            family_name: null,
            brin: null,
          },
        }),
      },
    };
  }

  private static getNameFromClaims(claims: ClaimsInterface) {
    return [
      claims.first_name,
      claims.tussenvoegsels ?? undefined,
      claims.family_name,
    ]
      .filter((n) => n)
      .join(' ');
  }

  private static getLicensesFromClaims(claims: ClaimsInterface) {
    if (!claims.licenses) {
      return [];
    }

    const licenses = claims.licenses
      .map(ToegangProvider.getLicenseValue)
      .filter((license) => license !== undefined) as Array<string>;

    return licenses;
  }

  private static getLicenseValue(license: LicenseInterface) {
    const { publisherInfo } = license.product;

    if (!publisherInfo) {
      return undefined;
    }

    const { description, value } = publisherInfo;

    if (!description || !value || description !== 'CPI') {
      return undefined;
    }

    return value;
  }
}
